/**
 * British Columbia coverage architecture.
 *
 * This file is deliberately independent from `service-areas.ts`: adding a city
 * here does not create a route, sitemap entry, office claim, or indexable page.
 * Publication and operational availability require their own explicit flags.
 */

export const bcCoverageLanguage = {
  emergency:
    "Emergency and urgent roofing response is available throughout the Lower Mainland, subject to crew availability and practical dispatch capability.",
  planned:
    "Scheduled roofing projects are available throughout British Columbia, subject to project scope, access, mobilization, logistics and scheduling.",
  mobilization:
    "Mobilization and minimum project requirements may apply to projects outside the Lower Mainland.",
  confirmation:
    "Contact Revolin Roofing to confirm availability for your project location and scope.",
  serviceAreaDisclaimer:
    "A listed community is a service area and does not imply that Revolin Roofing has a physical office there.",
} as const;

export type BcCoverageRegionId =
  | "lower-mainland-fraser-valley"
  | "sea-to-sky"
  | "sunshine-coast"
  | "greater-victoria"
  | "vancouver-island"
  | "okanagan"
  | "thompson-shuswap"
  | "kootenays"
  | "central-bc"
  | "northwest-bc"
  | "northeast-bc";

export type CoveragePublicationFlags = {
  /** A real route exists and may be resolved by the application. */
  routeEnabled: boolean;
  /** Search indexing is approved. Must never be inferred from eligibility. */
  indexable: boolean;
  /** Inclusion in generated sitemaps is approved. */
  sitemapEnabled: boolean;
};

export type BcCoverageRegion = {
  id: BcCoverageRegionId;
  name: string;
  emergencyEligible: boolean;
  plannedEligible: boolean;
  outsideLowerMainland: boolean;
  publication: CoveragePublicationFlags;
};

/**
 * Region hubs are configuration-only until Revolin approves substantive,
 * unique pages. This prevents the coverage model from mass-publishing thin
 * location pages.
 */
export const bcCoverageRegions = [
  {
    id: "lower-mainland-fraser-valley",
    name: "Lower Mainland / Fraser Valley",
    emergencyEligible: true,
    plannedEligible: true,
    outsideLowerMainland: false,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "sea-to-sky",
    name: "Sea-to-Sky",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "sunshine-coast",
    name: "Sunshine Coast",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "greater-victoria",
    name: "Greater Victoria",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "vancouver-island",
    name: "Vancouver Island",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "okanagan",
    name: "Okanagan",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "thompson-shuswap",
    name: "Thompson / Shuswap",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "kootenays",
    name: "Kootenays",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "central-bc",
    name: "Central BC",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "northwest-bc",
    name: "Northwest BC",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
  {
    id: "northeast-bc",
    name: "Northeast BC",
    emergencyEligible: false,
    plannedEligible: true,
    outsideLowerMainland: true,
    publication: { routeEnabled: false, indexable: false, sitemapEnabled: false },
  },
] as const satisfies readonly BcCoverageRegion[];

/** The protected IDs already published by `service-areas.ts`. */
export const EXISTING_SERVICE_AREA_IDS = [
  "vancouver",
  "burnaby",
  "coquitlam",
  "surrey",
  "richmond",
  "north-vancouver",
  "west-vancouver",
  "new-westminster",
  "port-coquitlam",
  "port-moody",
  "maple-ridge",
  "delta",
  "langley",
  "white-rock",
] as const;

export type ExistingServiceAreaId = (typeof EXISTING_SERVICE_AREA_IDS)[number];

type BcCoverageCityDefinition = {
  id: string;
  name: string;
  regionId: BcCoverageRegionId;
  emergencyEligible: boolean;
};

const BC_COVERAGE_CITY_DEFINITIONS = [
  // Lower Mainland / Fraser Valley
  {
    id: "vancouver",
    name: "Vancouver",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "burnaby",
    name: "Burnaby",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "richmond",
    name: "Richmond",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "surrey",
    name: "Surrey",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  { id: "delta", name: "Delta", regionId: "lower-mainland-fraser-valley", emergencyEligible: true },
  {
    id: "white-rock",
    name: "White Rock",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "new-westminster",
    name: "New Westminster",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "coquitlam",
    name: "Coquitlam",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "port-coquitlam",
    name: "Port Coquitlam",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "port-moody",
    name: "Port Moody",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "maple-ridge",
    name: "Maple Ridge",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "pitt-meadows",
    name: "Pitt Meadows",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "langley",
    name: "Langley",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "abbotsford",
    name: "Abbotsford",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "mission",
    name: "Mission",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "chilliwack",
    name: "Chilliwack",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  { id: "hope", name: "Hope", regionId: "lower-mainland-fraser-valley", emergencyEligible: true },
  {
    id: "north-vancouver",
    name: "North Vancouver",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },
  {
    id: "west-vancouver",
    name: "West Vancouver",
    regionId: "lower-mainland-fraser-valley",
    emergencyEligible: true,
  },

  // Scheduled / planned project coverage throughout British Columbia
  { id: "squamish", name: "Squamish", regionId: "sea-to-sky", emergencyEligible: false },
  { id: "whistler", name: "Whistler", regionId: "sea-to-sky", emergencyEligible: false },
  { id: "gibsons", name: "Gibsons", regionId: "sunshine-coast", emergencyEligible: false },
  { id: "sechelt", name: "Sechelt", regionId: "sunshine-coast", emergencyEligible: false },
  { id: "victoria", name: "Victoria", regionId: "greater-victoria", emergencyEligible: false },
  { id: "saanich", name: "Saanich", regionId: "greater-victoria", emergencyEligible: false },
  { id: "langford", name: "Langford", regionId: "greater-victoria", emergencyEligible: false },
  { id: "nanaimo", name: "Nanaimo", regionId: "vancouver-island", emergencyEligible: false },
  { id: "duncan", name: "Duncan", regionId: "vancouver-island", emergencyEligible: false },
  { id: "parksville", name: "Parksville", regionId: "vancouver-island", emergencyEligible: false },
  { id: "courtenay", name: "Courtenay", regionId: "vancouver-island", emergencyEligible: false },
  {
    id: "campbell-river",
    name: "Campbell River",
    regionId: "vancouver-island",
    emergencyEligible: false,
  },
  { id: "kelowna", name: "Kelowna", regionId: "okanagan", emergencyEligible: false },
  { id: "west-kelowna", name: "West Kelowna", regionId: "okanagan", emergencyEligible: false },
  { id: "vernon", name: "Vernon", regionId: "okanagan", emergencyEligible: false },
  { id: "penticton", name: "Penticton", regionId: "okanagan", emergencyEligible: false },
  { id: "kamloops", name: "Kamloops", regionId: "thompson-shuswap", emergencyEligible: false },
  { id: "salmon-arm", name: "Salmon Arm", regionId: "thompson-shuswap", emergencyEligible: false },
  { id: "revelstoke", name: "Revelstoke", regionId: "thompson-shuswap", emergencyEligible: false },
  { id: "cranbrook", name: "Cranbrook", regionId: "kootenays", emergencyEligible: false },
  { id: "nelson", name: "Nelson", regionId: "kootenays", emergencyEligible: false },
  { id: "castlegar", name: "Castlegar", regionId: "kootenays", emergencyEligible: false },
  { id: "trail", name: "Trail", regionId: "kootenays", emergencyEligible: false },
  { id: "prince-george", name: "Prince George", regionId: "central-bc", emergencyEligible: false },
  { id: "quesnel", name: "Quesnel", regionId: "central-bc", emergencyEligible: false },
  { id: "williams-lake", name: "Williams Lake", regionId: "central-bc", emergencyEligible: false },
  { id: "terrace", name: "Terrace", regionId: "northwest-bc", emergencyEligible: false },
  { id: "kitimat", name: "Kitimat", regionId: "northwest-bc", emergencyEligible: false },
  {
    id: "prince-rupert",
    name: "Prince Rupert",
    regionId: "northwest-bc",
    emergencyEligible: false,
  },
  { id: "fort-st-john", name: "Fort St. John", regionId: "northeast-bc", emergencyEligible: false },
  { id: "dawson-creek", name: "Dawson Creek", regionId: "northeast-bc", emergencyEligible: false },
] as const satisfies readonly BcCoverageCityDefinition[];

export type BcCoverageCityId = (typeof BC_COVERAGE_CITY_DEFINITIONS)[number]["id"];

export type BcCoverageCity = {
  id: BcCoverageCityId;
  slug: BcCoverageCityId;
  name: string;
  regionId: BcCoverageRegionId;
  province: "British Columbia";
  provinceCode: "BC";
  existingServiceArea: boolean;
  emergencyEligible: boolean;
  plannedEligible: true;
  /** Operational switch; eligibility alone must never promise dispatch. */
  emergencyDispatchEnabled: boolean;
  plannedProjectIntakeEnabled: boolean;
  mobilizationMayApply: boolean;
  minimumProjectRequirementsMayApply: boolean;
  publication: CoveragePublicationFlags;
};

const EXISTING_SERVICE_AREA_ID_SET = new Set<string>(EXISTING_SERVICE_AREA_IDS);

/**
 * The five Lower Mainland expansion cities are geographically eligible, but
 * emergency dispatch stays off until operations explicitly enables it. The 14
 * protected current IDs retain their existing publication and coverage state.
 */
export const bcCoverageCities: readonly BcCoverageCity[] = BC_COVERAGE_CITY_DEFINITIONS.map(
  (city) => {
    const existingServiceArea = EXISTING_SERVICE_AREA_ID_SET.has(city.id);
    const outsideLowerMainland = city.regionId !== "lower-mainland-fraser-valley";

    return {
      ...city,
      slug: city.id,
      province: "British Columbia",
      provinceCode: "BC",
      existingServiceArea,
      plannedEligible: true,
      emergencyDispatchEnabled: city.emergencyEligible && existingServiceArea,
      plannedProjectIntakeEnabled: true,
      mobilizationMayApply: outsideLowerMainland,
      minimumProjectRequirementsMayApply: outsideLowerMainland,
      publication: {
        routeEnabled: existingServiceArea,
        indexable: existingServiceArea,
        sitemapEnabled: existingServiceArea,
      },
    };
  },
);

export function isExistingServiceAreaId(id: string): id is ExistingServiceAreaId {
  return EXISTING_SERVICE_AREA_ID_SET.has(id);
}

export function findBcCoverageRegion(id: string | undefined): BcCoverageRegion | undefined {
  return bcCoverageRegions.find((region) => region.id === id);
}

export function findBcCoverageCity(id: string | undefined): BcCoverageCity | undefined {
  return bcCoverageCities.find((city) => city.id === id);
}

export function getBcCoverageCitiesByRegion(
  regionId: BcCoverageRegionId,
): readonly BcCoverageCity[] {
  return bcCoverageCities.filter((city) => city.regionId === regionId);
}

export function getPublishedBcCoverageCities(): readonly BcCoverageCity[] {
  return bcCoverageCities.filter(
    (city) =>
      city.publication.routeEnabled &&
      city.publication.indexable &&
      city.publication.sitemapEnabled,
  );
}

export function getEmergencyEligibleCities(): readonly BcCoverageCity[] {
  return bcCoverageCities.filter((city) => city.emergencyEligible);
}

export function getEmergencyDispatchCities(): readonly BcCoverageCity[] {
  return bcCoverageCities.filter((city) => city.emergencyEligible && city.emergencyDispatchEnabled);
}

export function getPlannedProjectCities(): readonly BcCoverageCity[] {
  return bcCoverageCities.filter(
    (city) => city.plannedEligible && city.plannedProjectIntakeEnabled,
  );
}

export function getCoverageLanguageForCity(id: BcCoverageCityId) {
  const city = findBcCoverageCity(id);

  if (!city) return undefined;

  return {
    emergency: city.emergencyEligible ? bcCoverageLanguage.emergency : undefined,
    planned: city.plannedEligible ? bcCoverageLanguage.planned : undefined,
    mobilization: city.mobilizationMayApply ? bcCoverageLanguage.mobilization : undefined,
    confirmation: bcCoverageLanguage.confirmation,
    serviceAreaDisclaimer: bcCoverageLanguage.serviceAreaDisclaimer,
  } as const;
}
